import React, { useEffect, useRef, useState } from 'react';
import './EVMap.scss';
import '../EVTooltip/EVTooltip.scss';
import { IAttribute, IEVChargingMap } from 'interfaces/chargingLocator';
import * as EVTooltip from '../EVTooltip/EVTooltip.ts';
import * as EVMapConstants from './EVMapConstants.tsx';
import { checkURL, trackEvent } from 'helpers/Analytics.ts';
import useInViewPort from 'hooks/useInViewport.ts';
import { useZipcodeCoordinates } from 'hooks/useZipcodeCoordinates.ts';
import { getZipcode, setZipcode } from 'helpers/Zipgate';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton.tsx';

const mapZoom = 14;

const EVMap = ({
  stations,
  hideInfobox,
  handleStationChange,
  selectedStation,
  blur,
  handleSearchCheck,
}: IEVChargingMap): JSX.Element => {
  const isHonda = process.env.REACT_APP_THEME === 'honda';
  const trackingObject = isHonda
    ? { Page: { page_name: '' } }
    : window.Acr.trackingObject || {};
  const dspToken = sessionStorage.getItem('dsp_token');
  const blurCheck = blur ? 'ev-blur' : '';
  const Microsoft = window.Microsoft;
  const staText = 'Search This Area';
  const mapRef: any = useRef();
  const bingMap: any = useRef();
  const currentPin: any = useRef();
  const [updatedLatitude, setUpdatedLatitude] = useState(0);
  const [updatedLongitude, setUpdatedLongitude] = useState(0);
  const targetRef = useRef<HTMLDivElement>(null);
  const inViewport = useInViewPort(targetRef, {
    threshold: 0,
  });
  const { zipcode, isLoading, fetchZipcode } = useZipcodeCoordinates(
    updatedLatitude,
    updatedLongitude
  );
  const mapClass = isHonda ? 'ev-map-honda' : 'ev-map';
  let currentZoom: number = 0;
  let firstZoom = false;

  const initMap = () => {
    const map = new Microsoft.Maps.Map(mapRef.current);
    map.setOptions(EVMapConstants.mapConstants.DEFAULT_OPTIONS);
    Microsoft.Maps.Events.addHandler(map, 'viewchangeend', endZoomEvent);
    Microsoft.Maps.Events.addHandler(map, 'viewchangestart', getZoom);
    return map;
  };

  useEffect(() => {
    if (updatedLatitude !== 0 && updatedLongitude !== 0) {
      handleSearchCheck();
      fetchZipcode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedLatitude, updatedLongitude]);

  useEffect(() => {
    if (zipcode && !isLoading) {
      setZipcode(zipcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcode, isLoading]);

  const handleSearchThisArea = () => {
    if (bingMap.current) {
      if (!isHonda) {
        trackEvent({
          label: 'csl:search this area',
          category: {
            includePageName: false,
            text: 'csl:map cta',
          },
          type: 'cta',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      } else {
        trackEvent({
          label: 'csl:search this area',
          category: {
            includePageName: false,
            text: 'csl:map cta',
          },
          type: 'cta',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      }
      const bounds = bingMap.current.getBounds();
      setUpdatedLatitude(bounds.center.latitude);
      setUpdatedLongitude(bounds.center.longitude);
    }
  };

  const getZoom = () => {
    currentZoom = bingMap.current.getZoom();
  };

  const endZoomEvent = () => {
    if (currentZoom > bingMap.current.getZoom()) {
      if (!isHonda) {
        trackEvent({
          label: 'csl:zoom out',
          category: {
            includePageName: false,
            text: 'csl:zoom',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      } else {
        trackEvent({
          label: 'csl:zoom out',
          category: {
            includePageName: false,
            text: 'csl:zoom',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      }
    } else {
      if (firstZoom) {
        if (currentZoom < bingMap.current.getZoom()) {
          if (!isHonda) {
            trackEvent({
              label: 'csl:zoom in',
              category: {
                includePageName: false,
                text: 'csl:zoom',
              },
              type: 'selector',
              modelSeries: 'zdx',
              modelYear: '2024',
              bodyStyle: 'suvs',
              environment: checkURL(trackingObject.Page?.full_url || ''),
              locator: {
                Page: {
                  ...trackingObject.Page,
                  page_friendly_url: trackingObject.Page?.full_url || '',
                },
                User: {
                  user_zipcode: getZipcode(),
                  user_status: dspToken ? 'logged_in' : 'not_logged_in',
                },
                GlobalMetadata: {
                  property_name: 'acura.com',
                  version_dl: 'V1.0',
                },
              },
            });
          } else {
            trackEvent({
              label: 'csl:zoom in',
              category: {
                includePageName: false,
                text: 'csl:zoom',
              },
              type: 'selector',
              modelSeries: 'zdx',
              modelYear: '2024',
              bodyStyle: 'suvs',
            });
          }
        }
      } else {
        firstZoom = true;
      }
    }
  };

  const updatePins = (zoom: number, map: any) => {
    let bestview;
    const infoboxTemplate = EVTooltip.htmlTemplate;
    const locations: string[] = [];
    if (stations?.length === 0) {
      const mapOptions = {
        zoom: zoom,
      };
      map.setView(mapOptions);
      return;
    }

    map.entities.clear();

    stations?.forEach(function addPoint(station, i) {
      var iconValue;
      if (station.attributes.comingSoon.length > 0) {
        iconValue = 0;
      } else {
        iconValue = checkSpeeds(station.attributes.chargingSpeed);
      }
      const iconArray = isHonda
        ? EVMapConstants.hondaIconArray
        : EVMapConstants.iconArray;

      locations[i] = new Microsoft.Maps.Location(
        station.latitude,
        station.longitude
      );

      //Create custom Pushpin using an SVG string.
      var pin = new Microsoft.Maps.Pushpin(locations[i], {
        icon: iconArray[iconValue],
        anchor: new Microsoft.Maps.Point(18, 36),
        catId: iconValue,
      });

      pin.customIndex = i;

      pin.metadata = {
        station: station,
      };

      //Add a click event handler to the pushpin.
      Microsoft.Maps.Events.addHandler(pin, 'mouseover', pushpinHover);

      //Add a click event handler to the pushpin.
      Microsoft.Maps.Events.addHandler(pin, 'mouseout', function () {
        infobox.setOptions({ visible: false });
      });

      //Add a click event handler to the pushpin.
      Microsoft.Maps.Events.addHandler(pin, 'click', function () {
        if (currentPin.current !== undefined) {
          currentPin.current.setOptions({
            icon: isHonda
              ? EVMapConstants.hondaIconArray[currentPin.current._options.catId]
              : EVMapConstants.iconArray[currentPin.current._options.catId],
            anchor: new Microsoft.Maps.Point(18, 36),
          });
        }
        currentPin.current = pin;
        handleStationChange(station);
        pin.setOptions({
          icon: isHonda
            ? EVMapConstants.hondaActiveIconArray[pin._options.catId]
            : EVMapConstants.activeIconArray[pin._options.catId],
          anchor: new Microsoft.Maps.Point(37, 70),
        });
        if (!isHonda) {
          trackEvent({
            label: `csl:station details:${pin.metadata.station.name}`,
            category: {
              includePageName: false,
              text: 'csl:station details expand',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
            environment: checkURL(trackingObject.Page?.full_url || ''),
            locator: {
              Page: {
                ...trackingObject.Page,
                page_friendly_url: trackingObject.Page?.full_url || '',
              },
              User: {
                user_zipcode: getZipcode(),
                user_status: dspToken ? 'logged_in' : 'not_logged_in',
              },
              GlobalMetadata: {
                property_name: 'acura.com',
                version_dl: 'V1.0',
              },
            },
          });
        } else {
          trackEvent({
            label: `csl:station details:${pin.metadata.station.name}`,
            category: {
              includePageName: false,
              text: 'csl:station details expand',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
          });
        }
      });

      //Add the pushpin to the map.
      map.entities.push(pin);
    });

    //Create an infobox at the center of the map but don't show it.
    const infobox = new Microsoft.Maps.Infobox(map.getCenter(), {
      visible: false,
      htmlContent: infoboxTemplate,
    });

    //Assign the infobox to a map instance.
    infobox.setMap(map);

    function pushpinHover(clickEvent: any) {
      infobox.setOptions({ visible: false });
      // Default tooltip position
      let offset = new Microsoft.Maps.Point(-40, 195);
      let windowSize = window.innerWidth < 1490 ? window.innerWidth : 1490;
      if (clickEvent.target.metadata.station && !hideInfobox) {
        if (clickEvent.point.x < 190 || clickEvent.point.x > windowSize - 280) {
          // Top Left Case
          if (clickEvent.point.x < 190) {
            offset =
              clickEvent.point.y <= 290
                ? new Microsoft.Maps.Point(20, -20)
                : new Microsoft.Maps.Point(20, 180);
          } else {
            //Top Right Case
            // 300px to give additional space to the tooltip 250px size.
            offset =
              clickEvent.point.y <= 290
                ? new Microsoft.Maps.Point(-300, -80)
                : new Microsoft.Maps.Point(-300, 230);
          }
        } else {
          // 265 value was defined based on the size of the tooltip being 250px
          // And additional space for cases where it can be close to the filters
          if (clickEvent.point.y <= 320) {
            offset =
              clickEvent.point.y <= 40
                ? new Microsoft.Maps.Point(-40, -130)
                : new Microsoft.Maps.Point(-40, -20);
          }
          // This case covers when the tooltip can be below the legend or near STA
          if (clickEvent.point.y >= 520) {
            offset =
              clickEvent.point.y >= 610
                ? new Microsoft.Maps.Point(-40, 320)
                : new Microsoft.Maps.Point(-40, 280);
          }
        }
        infobox.setLocation(clickEvent.target.getLocation());
        infobox.setOptions({
          visible: true,
          ignoreDelay: true,
          htmlContent: infoboxTemplate
            .replace('{title}', clickEvent.target.metadata.station.name)
            .replace('{address}', clickEvent.target.metadata.station.address)
            .replace('{city}', clickEvent.target.metadata.station.city)
            .replace(
              '{network}',
              clickEvent.target.metadata.station.network?.name
                ? `${clickEvent.target.metadata.station.network?.name} Network`
                : 'Network Name Unavailable'
            )
            .replace(
              '{PWAEA}',
              clickEvent.target.metadata.station.isEvApp
                ? isHonda
                  ? 'Pay with HondaLink app'
                  : 'Pay with Acura EV app'
                : ''
            ),
          showPointer: true,
          maxWidth: 286,
          offset: offset,
        });
      }
    }

    function checkSpeeds(data: Array<IAttribute>) {
      var result = 0;
      data.forEach((element) => {
        if (element.name === 'Slow' && result === 0) {
          result = 1;
        } else if (element.name === 'Fast' && result < 2) {
          result = 2;
        } else if (element.name === 'Very Fast' && result < 3) {
          result = 3;
        }
      });
      return result;
    }

    bestview = Microsoft.Maps.LocationRect.fromLocations(locations);

    const mapOptions = {
      center: bestview.center,
      zoom: zoom,
    };
    map.setView(mapOptions);
  };

  useEffect(() => {
    if (mapRef.current) {
      if (!bingMap.current) {
        bingMap.current = initMap();
        const zoom = mapZoom;
        updatePins(zoom, bingMap.current);
      } else {
        const zoom = bingMap.current.getZoom();
        updatePins(zoom, bingMap.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  useEffect(() => {
    if (inViewport) {
      if (!isHonda) {
        trackEvent({
          label: 'ev map:loaded',
          category: {
            includePageName: false,
            text: 'ev map',
          },
          type: 'section impression',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      } else {
        trackEvent({
          label: 'ev map:loaded',
          category: {
            includePageName: false,
            text: 'ev map',
          },
          type: 'section impression',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport]);

  useEffect(() => {
    if (bingMap.current) {
      bingMap.current.entities.clear();
      const zoom = bingMap.current.getZoom();
      updatePins(zoom, bingMap.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);

  useEffect(() => {
    if (selectedStation === undefined && currentPin.current !== undefined) {
      currentPin.current.setOptions({
        icon: isHonda
          ? EVMapConstants.hondaIconArray[currentPin.current._options.catId]
          : EVMapConstants.iconArray[currentPin.current._options.catId],
        anchor: new Microsoft.Maps.Point(18, 36),
      });
      currentPin.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation]);

  // This code was utilized when coordinates were the main way to ubicate on the map.
  // Now, it is being set by the collective of the stations and best view available.
  // useEffect(() => {
  //   if (coordinates && bingMap.current) {
  //     var newLocation = new Microsoft.Maps.Location(
  //       coordinates.latitude,
  //       coordinates.longitude
  //     );
  //     // bingMap.current.setView({ center: newLocation, zoom: mapZoom });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [coordinates]);

  return (
    <>
      <div ref={targetRef} className={`${mapClass} ${blurCheck}`}>
        <div ref={mapRef} className={`${mapClass}__container dealer-map`}></div>
      </div>
      <LinkButton
        variant={isHonda ? LinkButtonType.evAutos : LinkButtonType.black}
        onClick={handleSearchThisArea}
        disabled={blur}
        classNames={{
          container: `${mapClass}__search`,
        }}
      >
        {staText}
      </LinkButton>
    </>
  );
};

export default EVMap;
